import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesManagersList, assignTicket } from "@/store/slices/ticketSlice";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader"; 

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;

export default function AssignTicketModal({ isOpen, onClose, ticketData, onSuccess }) {
    if (!isOpen) return null;

    const { t } = useTranslation("ticket");
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const { managersList = [], loading } = useSelector((state) => state.ticket || {});
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    const schema = useMemo(() => yup.object().shape({
        managerId: yup
            .number()
            .typeError(t("selectManagerError"))
            .required(t("selectManagerError")),
        issueDateTime: yup
            .string()
            .required(t("issueDateTimeRequired")),
        priority: yup
            .string()
            .oneOf(["Low", "Medium", "High"])
            .required(t("priorityRequired")),
        note: yup
            .string()
            .required(t("noteRequired"))
            .matches(notBlankRegex, t("noteWhitespaceError"))
            .matches(noHTMLRegex, t("noteHtmlError"))
            .matches(noCodeRegex, t("noteCodeError"))
            .max(250, t("noteMaxLength"))
    }), [t]);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: {
            managerId: undefined,
            issueDateTime: "",
            priority: "",
            note: ""
        },
        resolver: yupResolver(schema),
        mode: "onChange" 
    });

    const currentManagerId = watch("managerId");
    const currentNoteValue = watch("note") || "";
    const currentPriority = watch("priority");
    const currentIssueDateTime = watch("issueDateTime");

    const selectedManager = managersList.find(m => m.user_id === currentManagerId) || null;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            dispatch(getSalesManagersList());
        }
    }, [isOpen, dispatch]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleNoteInputSanitization = (e) => {
        let val = e.target.value;

        if (val.startsWith(" ")) {
            return;
        }

        if (val.length > 250) {
            val = val.substring(0, 250);
        }

        setValue("note", val, { shouldValidate: true });
    };

    const onSubmit = async (data) => {
        const payload = {
            ticket_id: ticketData?.id,
            assigned_to: data.managerId,
            priority: data.priority.toLowerCase(),
            notes: data.note,
            assigned_at: data.issueDateTime
        };

        try {
            const response = await dispatch(assignTicket(payload)).unwrap();
            
            setIsSubmitting(true);
            
            if (onSuccess) {
                await onSuccess();
            }
            
            Toaster.success(response?.message || t("ticketAssignedSuccess", { manager: selectedManager?.full_name || t("salesRep") }));
            
            reset(); 
            onClose();
        } catch (err) {
            Toaster.error(err?.message || t("ticketAssignFailed"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isSubmitting && <ShowLoader />}

            <div className={`fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-xs transition-opacity duration-300 ${isSubmitting ? "hidden" : ""}`}>
                <div className="w-full max-w-[540px] bg-white rounded-xl shadow-xl overflow-hidden mx-4 transform transition-all animate-in fade-in zoom-in-95 duration-200">
                    
                    <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                        <h3 className="text-[22px] font-semibold text-gray-900 tracking-tight">
                            {t("assignSalesManagerTitle")}
                        </h3>
                        <button 
                            type="button" 
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-800 p-1 rounded-md transition-colors"
                            disabled={loading || isSubmitting}
                        >
                            <XMarkIcon className="h-6 w-6 stroke-[2] text-red-400" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5" noValidate>
                        
                        <div className="space-y-2" ref={dropdownRef}>
                            <label className="block text-sm font-semibold text-gray-900">
                                {t("selectSalesManager")}
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    disabled={loading || isSubmitting}
                                    className={`w-full flex items-center justify-between bg-white border ${
                                        errors.managerId ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-gray-300 focus:ring-amber-500/20 focus:border-amber-500"
                                    } rounded-lg pl-4 pr-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition-all text-left`}
                                >
                                    {selectedManager ? (
                                        <div className="flex items-center gap-2.5">
                                            <img 
                                                src={selectedManager.profile_image} 
                                                alt={selectedManager.full_name} 
                                                className="w-7 h-7 rounded-full object-cover border border-gray-100"
                                                onError={(e) => { e.target.src = "https://placehold.co/32x32?text=SM"; }}
                                            />
                                            <span className="font-medium text-gray-800">{selectedManager.full_name}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">{t("selectManagerPlaceholder")}</span>
                                    )}
                                    <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
                                </button>

                                {isDropdownOpen && (
                                    <div className="absolute left-0 right-0 z-[10005] mt-1.5 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto isolate scrollbar-thin">
                                        {!managersList || managersList.length === 0 ? (
                                            <div className="p-4 text-sm text-center text-gray-400">{t("loadingManagers")}</div>
                                        ) : (
                                            managersList.map((m) => (
                                                <button
                                                    key={m.user_id}
                                                    type="button"
                                                    onClick={() => {
                                                        setValue("managerId", m.user_id, { shouldValidate: true });
                                                        setIsDropdownOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-amber-50/50 transition-colors ${
                                                        currentManagerId === m.user_id ? "bg-amber-50 font-semibold text-amber-900" : "text-gray-700"
                                                    }`}
                                                >
                                                    <img 
                                                        src={m.profile_image} 
                                                        alt={m.full_name} 
                                                        className="w-10 h-10 rounded-full object-cover border border-gray-100 shrink-0"
                                                        onError={(e) => { e.target.src = "https://placehold.co/32x32?text=SM"; }}
                                                    />
                                                    <span className="font-medium text-gray-800">{m.full_name}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {errors.managerId && (
                                <p className="text-xs font-medium text-red-500 mt-1 pl-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                    {errors.managerId.message}
                                </p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-900">
                                    {t("issueDateTimeLabel")}
                                </label>
                                <input
                                    type="datetime-local"
                                    value={currentIssueDateTime}
                                    {...register("issueDateTime")}
                                    disabled={loading || isSubmitting}
                                    className={`w-full bg-white border ${
                                        errors.issueDateTime ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-gray-300 focus:ring-amber-500/20 focus:border-amber-500"
                                    } rounded-lg px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition-all cursor-pointer`}
                                />
                                {errors.issueDateTime && (
                                    <p className="text-xs font-medium text-red-500 mt-1 pl-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                        {errors.issueDateTime.message}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-900">
                                    {t("priorityLabel")}
                                </label>
                                <div className="relative">
                                    <select
                                        value={currentPriority}
                                        {...register("priority")}
                                        disabled={loading || isSubmitting}
                                        className={`w-full bg-white border ${
                                            errors.priority ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-gray-300 focus:ring-amber-500/20 focus:border-amber-500"
                                        } rounded-lg pl-4 pr-10 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 appearance-none transition-all cursor-pointer`}
                                    >
                                        <option value="" disabled>{t("selectPriorityPlaceholder")}</option>
                                        <option value="Low">{t("priorityLow")}</option>
                                        <option value="Medium">{t("priorityMedium")}</option>
                                        <option value="High">{t("priorityHigh")}</option>
                                    </select>
                                    <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                                </div>
                                {errors.priority && (
                                    <p className="text-xs font-medium text-red-500 mt-1 pl-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                        {errors.priority.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-semibold text-gray-900">
                                    {t("noteLabel")}
                                </label>
                                <span className={`text-xs font-medium ${currentNoteValue.length >= 250 ? "text-red-500" : "text-gray-400"}`}>
                                    {currentNoteValue.length}/250
                                </span>
                            </div>
                            <textarea
                                rows={4}
                                value={currentNoteValue}
                                onChange={handleNoteInputSanitization}
                                disabled={loading || isSubmitting}
                                placeholder={t("enterNotePlaceholder")}
                                className={`w-full bg-white border ${
                                    errors.note ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-gray-300 focus:ring-amber-500/20 focus:border-amber-500"
                                } rounded-lg p-4 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all resize-none`}
                            />
                            {errors.note && (
                                <p className="text-xs font-medium text-red-500 mt-0.5 pl-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                    {errors.note.message}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-50">
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-[140px] text-center bg-[#EAE8E5] hover:bg-gray-300 text-gray-800 text-sm font-semibold py-3 px-4 rounded-md transition-colors duration-150"
                                disabled={loading || isSubmitting}
                            >
                                {t("notNowButton")}
                            </button>
                            <button
                                type="submit"
                                className="w-[140px] text-center bg-[#F0B100] hover:bg-[#D69E00] text-gray-900 text-sm font-semibold py-3 px-4 rounded-md transition-colors duration-150 flex items-center justify-center gap-2"
                                disabled={loading || isSubmitting}
                            >
                                {loading || isSubmitting ? (
                                    <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    t("assignNowButton")
                                )}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </>
    );
}